import { Meta } from './common'
import { CaseStudyListResponse, QuestionResponse } from './question'
import { Result } from './status'
import { PagingInfo } from './user'

export interface PracticeTest {
  id: string
  is_mock_exam: boolean
  class_id: string
  owner: string
  name: string
  general_information?: string
  status: string
  cummulative_time: number
  cummulative_score: number
  created_at: string
  updated_at: string
  module_id: number
}

export interface PracticeTestBody {
  question_ids: number[]
  is_mock_exam: boolean
  practice_test_name: string
  general_information?: string
  module_id: number
  owner_id: number
  /** Total test duration in minutes (sent on create as well as update). */
  duration?: number
  reading_timer_enabled?: boolean
  reading_duration_seconds?: number
}

export interface UpdatePracticeTestBody {
  name: string
  status: PracticeTestStatus
  general_information?: string
  duration: number
  reading_timer_enabled?: boolean
  reading_duration_seconds?: number
  sections: UpdatePracticeTestSection[]
}

export interface UpdatePracticeTestSection {
  section: string
  question_ids: number[]
}

export interface UpdatePracticeTestResponse {
  id: number
  name: string
  status: PracticeTestStatus
  general_information?: string
  cummulative_time: number
  cummulative_score: number
}

export interface PracticeTestSection {
  name: string
  total_marks: number
  total_time: number
  total_questions: number
  questions: QuestionResponse
  case_studies: CaseStudyListResponse
}

export interface PracticeTestDetail {
  pt_id: number
  pt_name: string
  module_name: string
  general_information?: string
  status: string
  owner: string
  owner_id?: number
  sections: PracticeTestSection[]
  duration?: number
  reading_timer_enabled?: boolean
  reading_duration_seconds?: number
  class_name?: string
  class_id?: number
  created_at?: string
  is_unlimited_time?: boolean
  module_id?: number
  creator_type?: string
}

export interface PracticeTestDetailParams {
  practice_test_id: string
  size?: number
  page?: number
}

export enum CreatorType {
  COMMON = 'common',
  TUTOR_CREATED = 'tutor_created',
  STUDENT_CREATED = 'student_created',
  TUTOR = 'tutor',
  STUDENT = 'student',
}

export enum PracticeTestType {
  COMMON = 'common',
  TUTOR_CREATED = 'tutor_created',
  STUDENT_CREATED = 'student_created',
  TUTOR = 'tutor',
  STUDENT = 'student',
}

export enum PracticeTestStatus {
  PUBLISHED = 'published',
  DRAFT = 'draft',
  INACTIVE = 'inactive',
}

export interface PracticeTestList {
  id: number
  name: string
  owner: number
  participants: number
  unmarked: number
  creator_type: CreatorType
  avg_ai_marks: number
  avg_tutor_marks: number
  total_students: number
  created_at: string
  updated_at: string
  total_questions: number
  total_marks: number
  total_time: number
  /** Present on list responses when status filter is omitted (e.g. "All" tab). */
  status_type?: string | null
}

export function formatPracticeTestStatusTypeLabel(
  statusType?: string | null
): string {
  if (statusType == null || statusType === '') return 'N/A'
  if (statusType.toLowerCase() === PracticeTestStatus.INACTIVE) {
    return 'deactivated'
  }
  return statusType.replace(/_/g, ' ')
}

export interface PracticeTestListParams {
  class_id: number
  page: number
  limit: number
  is_practice?: boolean | null
  creator_type: CreatorType | null
  status: PracticeTestStatus | null
  search: string
}

export interface PracticeTestListResponse {
  data: PracticeTestList[]
  meta: Meta
}

export interface DuplicatePracticeTestBody {
  practice_test_id: number
  practice_test_name: string
  general_information?: string
  class_id: number[]
  user_id: number
  module_id: number
}

export interface PracTestStatRes {
  total_practice_tests: number
  total_mock_exams: number
  avg_pt_marks: number
  avg_mock_exam_marks: number
}

export interface PracTestDetailRes {
  practice_test_id: number
  participants: number
  attempts: number
  avg_ai_marks?: number
  avg_tutor_marks?: number
  avg_ai_score?: number
  avg_tutor_score?: number
  ai_alightment_rate?: number
  alignment_rate?: number
  pass_rate?: number
  merit_rate?: number
  distinction_rate?: number
}

export interface TestmanageRes {
  data: TestManageItem[]
  meta: PagingInfo
}

export interface TestManageItem {
  id: number
  name: string
  class_id: number
  participants: number
  total_students: number
  total_attempts: number
  creator_type: CreatorType
  created_at: string
  updated_at: string
  total_questions: number
  total_marks: number
  total_time: number
  class_info: ClassInfo[]
  avg_ai_marks: number
  avg_tutor_marks: number
  avg_mark: {
    ai: number
    tutor: number
  }
  owner: number
  unmarked: number
}

export interface ClassInfo {
  id: number
  name: string
}

export interface PracTestOverviewRes {
  practice_test_id: number
  participants: number
  attempts: number
  avg_ai_marks?: number
  avg_tutor_marks?: number
  avg_ai_score?: number
  avg_tutor_score?: number
  ai_alightment_rate: number | null
  alignment_rate: number | null
  pass_rate: number | null
  merit_rate: number | null
  distinction_rate: number | null
}

export interface PracTestListParticipantsParams {
  practice_test_id: number
  class_id: number | null | string
  search: string | null
  result?: Result | null
  page?: number
  limit?: number
}

export interface PracTestListParticipants {
  practice_test_id: number
  practice_test_name: string | null
  created_by: string | null
  student_id: number
  student_name: string | null
  last_activity: string | null
  total_attempts: number | null
  total_unmarked: number | null
  ai_marks: number | null
  tutor_marks: number | null
  result: Result | null
  created_at: string | null
  class_id: number
  class_name: string
}

export interface PracTestListParticipantsRes {
  data: PracTestListParticipants[]
  meta: Meta
}
